import { env } from 'lib/env';
import { REPORTS_SEARCH_INDEX } from 'lib/util/algolia';
import { classnames } from 'tailwindcss-classnames';
import { SortBy, useInstantSearch } from 'react-instantsearch-hooks-web';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { useSearchDictionary } from './util';
import { SearchWrapperProps } from './SearchTypes';
import { useMemo } from 'react';

type SearchSortItem = { label: string; value: string };

export type SearchSortProfile =
  | 'search-page'
  | 'search-listing'
  | 'reports'
  | 'product-list'
  | 'fee-schedule'
  | 'announcement'
  | 'reports-hidden-date'
  | 'announcement-hidden-date'
  | 'event-hidden-date-asc';

type SearchSortProps = {
  profile?: SearchSortProfile;
  hideNameSort?: boolean;
};

type SearchSortConfig = {
  editingLabel: string;
  items: SearchSortItem[];
  hidden?: boolean;
  wrapperClasses: string[];
  labelClasses: string[];
  selectClasses: string[];
};

const defaultWrapperClasses = ['flex', 'items-center', 'mb-4', 'sm:mb-2'];
const searchSortEditingLabel = 'Search Sort';
const reportsSortEditingLabel = 'Reports Sort';
const defaultLabelClasses = [
  'text-blue-navy',
  'text-body',
  'font-semibold',
  'mr-6',
  'min-w-[50px]',
  'lg:min-w-0',
];
const compactLabelClasses = [
  'text-blue-navy',
  'text-body',
  'font-medium',
  'mr-6',
  'min-w-[50px]',
  'text-sm',
  'text-black',
  'lg:min-w-0',
];
const defaultSelectClasses = ['border', 'border-gray-spunPearl', 'rounded-md', 'p-2', 'w-full'];
const compactSelectClasses = [
  'border',
  'border-gray-spunPearl',
  'rounded-md',
  'p-2',
  'w-full',
  'text-sm',
  'text-black',
];

const buildSearchSortConfig = ({
  dict,
  profile,
  hideNameSort,
  selectedType,
}: {
  dict: ReturnType<typeof useSearchDictionary>;
  profile: SearchSortProfile;
  hideNameSort: boolean;
  selectedType?: string;
}): SearchSortConfig => {
  const searchIndex = env.ALGOLIA_INDEX_NAME;
  const reportsIndex = REPORTS_SEARCH_INDEX() ?? searchIndex;

  switch (profile) {
    case 'search-listing':
      return {
        editingLabel: searchSortEditingLabel,
        items: hideNameSort
          ? [
              { label: dict.sortDateDesc, value: `${searchIndex}_date_desc` },
              { label: dict.sortDateAsc, value: `${searchIndex}_date_asc` },
            ]
          : [
              { label: dict.sortNameAsc, value: `${searchIndex}_name_asc` },
              { label: dict.sortNameDesc, value: `${searchIndex}_name_desc` },
              { label: dict.sortDateDesc, value: `${searchIndex}_date_desc` },
              { label: dict.sortDateAsc, value: `${searchIndex}_date_asc` },
            ],
        wrapperClasses: defaultWrapperClasses,
        labelClasses: compactLabelClasses,
        selectClasses: compactSelectClasses,
      };
    case 'reports':
      return {
        editingLabel: reportsSortEditingLabel,
        items: [
          { label: dict.sortNewest, value: `${reportsIndex}_date_desc` },
          { label: dict.sortNameAsc, value: `${reportsIndex}_name_asc` },
          { label: dict.sortNameDesc, value: `${reportsIndex}_name_desc` },
        ],
        wrapperClasses: defaultWrapperClasses,
        labelClasses: defaultLabelClasses,
        selectClasses: compactSelectClasses,
      };
    case 'product-list':
      return {
        editingLabel: searchSortEditingLabel,
        items: [
          { label: dict.sortNewest, value: `${searchIndex}_date_desc` },
          { label: dict.sortOldest, value: `${searchIndex}_date_asc` },
          { label: dict.sortNameAsc, value: `${searchIndex}_name_asc` },
          { label: dict.sortNameDesc, value: `${searchIndex}_name_desc` },
        ],
        wrapperClasses: defaultWrapperClasses,
        labelClasses: compactLabelClasses,
        selectClasses: compactSelectClasses,
      };
    case 'fee-schedule':
      return {
        editingLabel: reportsSortEditingLabel,
        items: [
          { label: dict.sortNewest, value: `${reportsIndex}_date_desc` },
          { label: dict.sortNameAsc, value: `${reportsIndex}_name_asc` },
          { label: dict.sortNameDesc, value: `${reportsIndex}_name_desc` },
        ],
        wrapperClasses: ['flex', 'items-center', 'lg:mb-0', 'sm:mb-2'],
        labelClasses: compactLabelClasses,
        selectClasses: compactSelectClasses,
      };
    case 'announcement':
      return {
        editingLabel: searchSortEditingLabel,
        items: [
          { label: dict.announcementSortRelevant, value: searchIndex },
          { label: dict.announcementSortDate, value: `${searchIndex}_date_desc` },
        ],
        wrapperClasses: defaultWrapperClasses,
        labelClasses: defaultLabelClasses,
        selectClasses: defaultSelectClasses,
      };
    case 'reports-hidden-date':
      return {
        editingLabel: reportsSortEditingLabel,
        items: [{ label: dict.sortNewest, value: `${reportsIndex}_date_desc` }],
        hidden: true,
        wrapperClasses: defaultWrapperClasses,
        labelClasses: defaultLabelClasses,
        selectClasses: defaultSelectClasses,
      };
    case 'announcement-hidden-date':
      return {
        editingLabel: searchSortEditingLabel,
        items: [
          {
            label: dict.announcementSortDate || dict.sortDateDesc,
            value: `${searchIndex}_date_desc`,
          },
          { label: dict.announcementSortRelevant || dict.sortRelevancy, value: searchIndex },
        ],
        hidden: true,
        wrapperClasses: defaultWrapperClasses,
        labelClasses: defaultLabelClasses,
        selectClasses: defaultSelectClasses,
      };
    case 'event-hidden-date-asc':
      return {
        editingLabel: searchSortEditingLabel,
        items: [{ label: dict.sortDateAsc, value: `${searchIndex}_date_asc` }],
        hidden: true,
        wrapperClasses: defaultWrapperClasses,
        labelClasses: defaultLabelClasses,
        selectClasses: defaultSelectClasses,
      };
    case 'search-page':
    default:
      return {
        editingLabel: searchSortEditingLabel,
        items:
          selectedType === 'employeee'
            ? [
                { label: dict.sortFirstNameAsc, value: `${searchIndex}_firstname_asc` },
                { label: dict.sortFirstNameDesc, value: `${searchIndex}_firstname_desc` },
                { label: dict.sortLastNameAsc, value: `${searchIndex}_lastname_asc` },
                { label: dict.sortLastNameDesc, value: `${searchIndex}_lastname_desc` },
              ]
            : [
                { label: dict.sortRelevancy, value: `${searchIndex}` },
                { label: dict.sortDate, value: `${searchIndex}_date_desc` },
              ],
        wrapperClasses: defaultWrapperClasses,
        labelClasses: defaultLabelClasses,
        selectClasses: defaultSelectClasses,
      };
  }
};

/**
 * A wrapper component for rendering a sortable search UI element.
 * It displays a label and wraps its children with styling for layout and alignment.
 *
 * @param {SearchWrapperProps & { sortLabel: string }} props - The props for the component.
 * @param {React.ReactNode} props.children - The child components to be rendered inside the wrapper.
 * @param {string} props.sortLabel - The label text to display for the sort functionality.
 *
 * @returns {JSX.Element} A styled wrapper containing the sort label and children.
 */
const SearchSortWrapper = ({
  children,
  sortLabel,
  wrapperClasses,
  labelClasses,
}: SearchWrapperProps & {
  sortLabel: string;
  wrapperClasses: string[];
  labelClasses: string[];
}): JSX.Element => {
  return (
    <div className={wrapperClasses.join(' ')}>
      <span className={labelClasses.join(' ')}>{sortLabel}</span>
      {children}
    </div>
  );
};

const SearchSort = ({
  profile = 'search-page',
  hideNameSort = false,
}: SearchSortProps): JSX.Element | null => {
  const { indexUiState: { menu: { type: selectedType } = {} } = {} } = useInstantSearch();
  const { sitecoreContext } = useSitecoreContext();
  const dict = useSearchDictionary();

  const config = useMemo(
    () =>
      buildSearchSortConfig({
        dict,
        profile,
        hideNameSort,
        selectedType,
      }),
    [dict, hideNameSort, profile, selectedType]
  );

  if (sitecoreContext.pageEditing === true) {
    if (config.hidden) {
      return null;
    }

    return (
      <SearchSortWrapper
        sortLabel={config.editingLabel}
        wrapperClasses={config.wrapperClasses}
        labelClasses={config.labelClasses}
      />
    );
  }

  const sortBy = (
    <SortBy
      classNames={{
        root: classnames(config.hidden ? 'hidden' : 'w-full'),
        select: `${config.selectClasses.join(' ')}${config.hidden ? ' hidden' : ''}`,
      }}
      items={config.items}
    />
  );

  if (config.hidden) {
    return sortBy;
  }

  return (
    <SearchSortWrapper
      sortLabel={dict.sort}
      wrapperClasses={config.wrapperClasses}
      labelClasses={config.labelClasses}
    >
      {sortBy}
    </SearchSortWrapper>
  );
};

export default SearchSort;
